import * as React from "react";

import { EntityType, ServiceAccount, ServiceAccountDraft } from "../../../../models";
import { Container } from "../../../Container";
import { addNewRouteFor, editRouteFor, appNavigator } from "../../../routes";

import { EditCreateEntityRoute, EditCreateEntityProps } from "../EditCreate/EditCreateEntityRoute";
import { NewServiceAccount } from "./NewServiceAccount";
import { ServiceAccountGrid } from "./ServiceAccountGrid";

import { EditServiceAccountActions } from "./EditServiceAccountActions";
import { EditServiceAccountStore } from "./EditServiceAccountStore";
import { EditCreateEntityActions } from "../../../actions/EditCreateEntityActions";
import { ListStore } from "../../../flux/Grid/ListStore";
import { SelectedItemsActions } from "../../../flux/Grid/SelectedItemsActions";
import { SelectedItemsStore } from "../../../flux/Grid/SelectedItemsStore";
import { DeleteObjectsActions } from "../../../flux/DeleteObjects/DeleteObjectsActions";
import { FlagStore } from "../../../flux/Flag/FlagStore";
import { ErrorStore } from "../../../flux/Error/ErrorStore";
import { Button } from "../../../../components/Buttons";
import { Toolbar } from "../../Toolbar";
import { SearchProps } from "../SearchProps";
import { ErrorActions } from "../../../flux/Error/ErrorActions";
import { twoButtonDialogConfig } from "../../../utils/configure-store";
import { NoResults } from "../../Table/NoResults";
import { TableTitle } from "../../Table/TableTitle";
import { LabelStore } from "../../../flux/Label/LabelStore";
import { Polyglot } from "../../../../polyglot";
import { StickyMediator } from "../../../utils/StickyMediator";
import { GenericSearchWidget } from "../Search/GenericSearchWidget";
import { TrashButtonWidget } from "../../TrashButtonWidget";
import { DeleteConfirmationWidget, DeleteItemsProps } from "../../DeleteConfirmationWidget";
import { Command, Command1 } from "../../../../Command";

const appStyles = require('../../../app.scss');
const tableStyles = require('../../../../components/Table.scss');

interface TabProps
{
    polyglot: Polyglot;
    tableTitleStore: LabelStore;
    listStore: ListStore<ServiceAccount>;
    selectedItemsStore: SelectedItemsStore<ServiceAccount>;
    
    loadDivisionInProgress: FlagStore;
    initUpsert: Command;

    editStore: EditServiceAccountStore;
    upsertErrorStore: ErrorStore;
    upsertInProgressStore: FlagStore;
    upsertCommand: Command1<ServiceAccountDraft>;

    isAdmin: boolean;
    isDivisionAware: boolean;
}

type Props = TabProps & SearchProps & DeleteItemsProps
export class ServiceAccountRoute extends React.Component<Props>
{
    private readonly errorActions = new ErrorActions();
    private readonly editActions = new EditServiceAccountActions();
    private readonly editCreateEntityActions = new EditCreateEntityActions();
    private readonly selectedItemsActions = new SelectedItemsActions();
    private readonly stickyMediator = new StickyMediator();

    componentDidMount()
    {
        this.props.refresher.run();
    }
    
    render()
    {
        const { isAdmin, polyglot } = this.props
        const createForm = (props: Partial<EditCreateEntityProps>) => (
            <Container merge stores={twoButtonDialogConfig(this.props.upsertInProgressStore, this.props.upsertErrorStore)}>
                <EditCreateEntityRoute title={props.title} okTitle={props.okTitle} path={props.path}
                    cancelTitle="Cancel"
                    renderEdit={() => (
                        <Container
                            merge stores={[this.props.editStore, [this.props.loadDivisionInProgress, store => ({ loadDivisionsInProgress: store.isSet() }) ]]}
                            actions={this.editActions}
                            onMount={this.props.initUpsert.execute}>
                                <NewServiceAccount polyglot={polyglot} loadDivisionsInProgress={null} />
                        </Container>
                    )}
                    cancel={() => {
                        appNavigator.goToEntityTab(entityType);
                        this.editCreateEntityActions.rejectDraft(entityType);
                    }}
                    ok={() => this.props.upsertCommand.execute(this.props.editStore.getState().draft)}
                />
            </Container>
        );
        
        const entityType = EntityType.ServiceAccount;
        const disableInject = {
            disabled: () => this.props.selectedItemsStore.getState().selected.size === 0
        };

        return (
            <div>
                {isAdmin && createForm({title: polyglot.t('service_account.create_title'), okTitle: polyglot.t('button.create'), path: addNewRouteFor(entityType)})}
                {isAdmin && createForm({title: polyglot.t('service_account.edit_title'), okTitle: polyglot.t('button.save'), path: editRouteFor(entityType)})}
                <DeleteConfirmationWidget {...this.props} entityType={entityType} title={polyglot.t('service_account.del_confirmation_title')}>
                    <span className={appStyles.ConfirmationContainer}>{polyglot.t('service_account.del_confirmation_msg')}</span>
                </DeleteConfirmationWidget>
                <div>
                    <GenericSearchWidget {...this.props} />
                    <div className={appStyles.StickyControlPanel} ref={this.stickyMediator.onToolbox}>
                        <Toolbar>
                        { isAdmin &&
                            <Button type="success" leftIcon="plus" onClick={() => {
                                this.editCreateEntityActions.rejectDraft(entityType);
                                this.errorActions.clearError(this.props.upsertErrorStore.dispatchToken);
                                appNavigator.goToAddNewEntityTab(entityType);
                            }}>{polyglot.t('button.new')}</Button>
                        }
                        { isAdmin && <TrashButtonWidget entityType={EntityType.ServiceAccount} {...this.props} /> }
                        </Toolbar>
                        <TableTitle polyglot={polyglot} labelStore={this.props.tableTitleStore} />
                    </div>
                    <Container merge stores={[this.props.listStore, this.props.selectedItemsStore]}>
                        <ServiceAccountGrid
                            className={tableStyles.StickyTable}
                            polyglot={polyglot}
                            isAdmin={isAdmin}
                            isDivisionAware={this.props.isDivisionAware}
                            items={null} selected={null} allSelected={null} // will be injected
                            onEdit={(datum, row) => {
                                this.editCreateEntityActions.setEntityToEdit(datum, entityType);
                                this.errorActions.clearError(this.props.upsertErrorStore.dispatchToken);
                                appNavigator.goToEditEntityTab(entityType);
                            }}
                            toggle={(datum, row) => this.selectedItemsActions.toggleItem((datum as ServiceAccount).id, entityType) }
                            toggleAll={() => this.selectedItemsActions.toggleAll(entityType)}
                            onThead={this.stickyMediator.onThead}
                        />
                    </Container>
                    <NoResults polyglot={polyglot} listStore={this.props.listStore} />
                </div>
            </div>
        );
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/ServiceAccount/ServiceAccountRoute.tsx